import { ROLE_SCOPE } from '@klareospace/pharos-lib-types';
import type { CurrentUserResponse, UserScope } from '@klareospace/pharos-lib-types';
import { usePermissions } from '@klareospace/pharos-lib-front-utils';
import { getParentSpaceIdsFromResolvedScopes } from '@klareospace/pharos-lib-utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseUserScopesReturn {
  /** True when the user has permission to access sites (sites:list) */
  canAccessSites: boolean;
  /** True when the user has permission to access spaces (spaces:list) */
  canAccessSpaces: boolean;
  /** True when the user has permission to access tenant settings (tenants:read) */
  canAccessTenantSettings: boolean;
  /**
   * First site scope ID for site-only-scoped users (single-site, multi-site, or cross-space).
   * Set whenever `isSiteOnlyScoped` is true so that settings routes can redirect the user to
   * their first site without waiting for MasterContext to select one.
   * null for all other user types (space-scoped, tenant-scoped, or still loading).
   */
  firstSiteId: string | null;
  /**
   * The first accessible space ID for site-only scoped users whose site scopes span multiple
   * parent spaces (cross-space-sites). Used by useMasterAutoInit to auto-select a space so that
   * settings navigation and breadcrumbs work before the user has made an explicit selection.
   * null for single-space site-only users (use singleSpaceId instead) and all other user types.
   */
  firstSpaceId: string | null;
  /** True when the user has access to more than one site */
  hasMultipleSites: boolean;
  /** True when the user has access to more than one space */
  hasMultipleSpaces: boolean;
  /** Whether the scope data is still loading */
  isLoading: boolean;
  /**
   * True when the user has one or more SITE-level scopes and no SPACE or TENANT scope.
   * Used to prevent routing these users to the space detail settings page, which they
   * cannot load (the backend scope filter maps SITE scopes to siteId, not spaceId).
   */
  isSiteOnlyScoped: boolean;
  /** True when the user has tenant-level access (empty scopes or tenant-level scope present) */
  isTenantScoped: boolean;
  /**
   * True when the user should land on the sites tab instead of the general/space tab.
   * This applies to users who have sites:list but NOT spaces:list — the general tab
   * requires spaces:list, so they must be routed to the sites tab to avoid redirect loops.
   */
  preferSitesTab: boolean;
  /** ID of the single site the user has access to (only when hasMultipleSites is false and there is exactly one site) */
  singleSiteId: string | null;
  /** ID of the single space the user has access to (only when hasMultipleSpaces is false and there is exactly one space) */
  singleSpaceId: string | null;
}

/** Internal shape returned by useUserScopesQueries — not exported. */
interface UserScopesQueries {
  hasMultipleSites: boolean;
  hasMultipleSpaces: boolean;
  isSiteOnlyScoped: boolean;
  isTenantScoped: boolean;
  siteCount: number;
  siteDetailSpaceId: string | null;
  siteScopes: UserScope[];
  spaceCount: number;
  spaceScopes: UserScope[];
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Derives sidebar navigation scoping values from the provided user's scope list.
 *
 * The caller is responsible for fetching the user (typically via `useCurrentUser`)
 * and passing it here — this avoids a duplicate fetch when the parent already
 * holds the user object.
 *
 * - Tenant Admins (empty scopes or tenant-level scope) use scopeSummary.spaceCount
 *   so their space/site totals reflect the full tenant catalogue.
 * - Scoped users derive counts directly from scopeSummary or their scope array.
 *
 * Output is consumed by `useMasterAutoInit` to call selectSpace()/selectSite()
 * on MasterContext, and by `buildMasterContextSettingsNavItems` in navigation.ts
 * for sidebar URL building.
 */
export function useUserScopes(user: CurrentUserResponse | null | undefined): UseUserScopesReturn {
  const { hasPermission } = usePermissions();
  const queries = useUserScopesQueries(user);

  // For site-only scoped users whose site scopes span multiple parent spaces,
  // derive hasMultipleSpaces and singleSpaceId from resolvedScopes instead of
  // from spaceScopes.length (which is always 0 for site-only users).
  const uniqueParentSpaceCount = computeUniqueParentSpaceCount(queries.isSiteOnlyScoped, user?.resolvedScopes);
  const isCrossSpaceSites = queries.isSiteOnlyScoped && uniqueParentSpaceCount > 1;
  const hasMultipleSpaces = isCrossSpaceSites ? true : queries.hasMultipleSpaces;
  const singleSpaceId = isCrossSpaceSites ? null : resolveSingleSpaceIdFromQueries(queries);

  // For cross-space-sites users, expose the first accessible parent space ID so that
  // useMasterAutoInit can auto-select it before the user makes an explicit selection.
  const firstSpaceId = isCrossSpaceSites ? resolveFirstParentSpaceId(user?.resolvedScopes) : null;

  const canAccessSites = hasPermission('sites:list');
  const canAccessSpaces = hasPermission('spaces:list');
  const canAccessTenantSettings = hasPermission('tenants:read');

  return {
    canAccessSites,
    canAccessSpaces,
    canAccessTenantSettings,
    firstSiteId: resolveFirstSiteId(queries.isSiteOnlyScoped, queries.siteScopes),
    firstSpaceId,
    hasMultipleSites: queries.hasMultipleSites,
    hasMultipleSpaces,
    isLoading: user === undefined,
    isSiteOnlyScoped: queries.isSiteOnlyScoped,
    isTenantScoped: queries.isTenantScoped,
    preferSitesTab: canAccessSites && !canAccessSpaces,
    singleSiteId: resolveSingleSiteIdFromQueries(queries),
    singleSpaceId,
  };
}

// ─── Private hook — query orchestration ───────────────────────────────────────

/**
 * Derives all scope counts and flags from the user object.
 * Counts now come from user.scopeSummary — no additional API calls needed.
 */
function useUserScopesQueries(user: CurrentUserResponse | null | undefined): UserScopesQueries {
  const isUserLoading = user === undefined;

  const scopes: UserScope[] = user?.scopes ?? [];
  // A user is tenant-scoped if their scopes are empty OR they have a tenant-level scope.
  // While the user is still loading (user === undefined), default to false.
  const isTenantScoped = resolveTenantScoped({ isUserLoading, scopes, user: user ?? null });

  const spaceScopes = scopes.filter((scope) => scope.level === ROLE_SCOPE.SPACE);
  const siteScopes = scopes.filter((scope) => scope.level === ROLE_SCOPE.SITE);

  // For site-scoped users (SITE_MANAGER, SITE_OPERATOR) who have one or more site scopes
  // and no space scopes, resolve the parent spaceId from resolvedScopes on the user object.
  // The backend enriches GET /users/me with parentSpaceId for each site-level scope,
  // so no additional API call is needed.
  const isSiteOnlyScoped = resolveSiteOnlyScoped({ isTenantScoped, isUserLoading, siteScopes, spaceScopes });
  const siteDetailSpaceId = resolveSiteDetailSpaceId(isSiteOnlyScoped, user?.resolvedScopes);

  // Counts come directly from scopeSummary — no API calls needed.
  const { siteCount, spaceCount } = resolveCountsFromScopeSummary(user);

  return {
    hasMultipleSites: deriveHasMultipleSites({ isTenantScoped, siteCount }),
    hasMultipleSpaces: spaceCount > 1,
    isSiteOnlyScoped,
    isTenantScoped,
    siteCount,
    siteDetailSpaceId,
    siteScopes,
    spaceCount,
    spaceScopes,
  };
}

// ─── Private helpers ──────────────────────────────────────────────────────────

/**
 * Returns the first site scope ID for site-only-scoped users, or null for all
 * other user types. Extracted to keep `useUserScopes` complexity within limits.
 */
function resolveFirstSiteId(isSiteOnlyScoped: boolean, siteScopes: UserScope[]): string | null {
  if (!isSiteOnlyScoped) return null;
  return siteScopes[0]?.id ?? null;
}

/**
 * Resolves the parent spaceId for site-only scoped users from their resolvedScopes.
 * Returns null for all other user types.
 */
function resolveSiteDetailSpaceId(
  isSiteOnlyScoped: boolean,
  resolvedScopes: CurrentUserResponse['resolvedScopes'] | undefined,
): string | null {
  if (!isSiteOnlyScoped) return null;
  return resolvedScopes?.find((rs) => rs.level === ROLE_SCOPE.SITE)?.parentSpaceId ?? null;
}

interface ScopeCounts {
  siteCount: number;
  spaceCount: number;
}

/**
 * Extracts space and site counts from the user's scopeSummary.
 * Defaults to 0 for both when unavailable.
 */
function resolveCountsFromScopeSummary(user: CurrentUserResponse | null | undefined): ScopeCounts {
  return {
    siteCount: user?.scopeSummary?.siteCount ?? 0,
    spaceCount: user?.scopeSummary?.spaceCount ?? 0,
  };
}

/**
 * For site-only scoped users, counts distinct parent spaces from resolvedScopes.
 * Returns 0 for tenant-scoped users or when resolvedScopes is unavailable.
 */
function computeUniqueParentSpaceCount(
  isSiteOnlyScoped: boolean,
  resolvedScopes: CurrentUserResponse['resolvedScopes'] | undefined,
): number {
  if (!isSiteOnlyScoped || !resolvedScopes) return 0;
  return getParentSpaceIdsFromResolvedScopes(resolvedScopes).length;
}

/**
 * Returns the first parent space ID found in resolvedScopes for cross-space-sites users.
 * Used to pre-select a space in MasterContext before the user makes an explicit selection.
 * Returns null when resolvedScopes is unavailable or has no site-level entries with a parentSpaceId.
 */
function resolveFirstParentSpaceId(resolvedScopes: CurrentUserResponse['resolvedScopes'] | undefined): string | null {
  if (!resolvedScopes) return null;
  return getParentSpaceIdsFromResolvedScopes(resolvedScopes)[0] ?? null;
}

interface ResolveTenantScopedParams {
  isUserLoading: boolean;
  scopes: UserScope[];
  user: { scopes: UserScope[] } | null;
}
function resolveTenantScoped({ isUserLoading, user, scopes }: ResolveTenantScopedParams): boolean {
  if (isUserLoading || user === null) return false;
  return scopes.length === 0 || scopes.some((scope) => scope.level === ROLE_SCOPE.TENANT);
}

interface ResolveSiteOnlyScopedParams {
  isUserLoading: boolean;
  isTenantScoped: boolean;
  siteScopes: UserScope[];
  spaceScopes: UserScope[];
}

/**
 * Returns true when the user is a pure site-scoped user: not loading, not tenant-scoped,
 * has no space-level scopes, and has one or more site-level scopes.
 * Used to decide whether to derive the parent spaceId from resolvedScopes.
 */
function resolveSiteOnlyScoped({
  isUserLoading,
  isTenantScoped,
  spaceScopes,
  siteScopes,
}: ResolveSiteOnlyScopedParams): boolean {
  return !isUserLoading && !isTenantScoped && spaceScopes.length === 0 && siteScopes.length >= 1;
}

interface HasMultipleSitesParams {
  isTenantScoped: boolean;
  siteCount: number;
}

/**
 * Returns true when the user effectively has access to more than one site.
 * Tenant admins always see all sites across all spaces, so we unconditionally
 * return true for them — no need to pre-fetch a full site count.
 */
function deriveHasMultipleSites({ isTenantScoped, siteCount }: HasMultipleSitesParams): boolean {
  if (isTenantScoped) return true;
  return siteCount > 1;
}

function resolveSingleSpaceIdFromQueries({
  hasMultipleSpaces,
  isSiteOnlyScoped,
  isTenantScoped,
  siteDetailSpaceId,
  spaceCount,
  spaceScopes,
}: UserScopesQueries): string | null {
  // Site-scoped users: use the parent spaceId resolved from resolvedScopes.
  if (isSiteOnlyScoped) return siteDetailSpaceId;

  if (hasMultipleSpaces || spaceCount !== 1) return null;

  // Tenant-scoped with a single space: we don't have the ID from scopeSummary alone,
  // return null so tenant users start at tenant level.
  if (isTenantScoped) return null;

  return spaceScopes[0]?.id ?? null;
}

function resolveSingleSiteIdFromQueries({
  hasMultipleSites,
  isTenantScoped,
  siteCount,
  siteScopes,
}: UserScopesQueries): string | null {
  if (hasMultipleSites || siteCount !== 1) return null;

  if (isTenantScoped) {
    // Tenant admins with 1 site: we'd need a full site fetch — return null (use list).
    return null;
  }

  // Site-scoped users (SITE_MANAGER, SITE_OPERATOR) have their site ID in siteScopes.
  return siteScopes[0]?.id ?? null;
}
